import React from "react";
import { useDispatch } from "react-redux";
import { Badge, OverlayTrigger, Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { Invoice2 } from "src/docuclipper/DocuclipperTypes";
import { setLiveConsistency } from "src/redux/reducers/InvoiceManager";
import {
  checkInvoiceConsistency,
  formatDelta,
  InvoiceConsistency,
} from "./invoiceConsistency";

type Props = {
  invoice: Pick<Invoice2, "total" | "subTotal" | "tax" | "lines"> | null | undefined;
  // When true, publish this invoice's reconciliation state to
  // InvoiceManager.liveConsistency so sibling components outside the editor's
  // Formik (the Export & Sync sidebar) can reflect unsaved edits in lockstep
  // with this badge. Pass on the live, `values`-fed badge inside the QBO/Xero
  // form; leave off for store-blob badges (e.g. the Excel path).
  publishLive?: boolean;
};

function buildDetails(c: InvoiceConsistency): React.ReactNode {
  const items: string[] = [];
  if (c.headerMismatch) {
    items.push(
      `Subtotal + Tax (${(c.subTotal + c.tax).toFixed(2)}) ≠ Total (${c.total.toFixed(
        2
      )}). Δ ${formatDelta(c.headerDelta)}.`
    );
  }
  if (c.linesMismatch) {
    items.push(
      `Line items sum (${c.linesSum.toFixed(2)}) ≠ Subtotal (${c.subTotal.toFixed(
        2
      )}). Δ ${formatDelta(c.linesDelta)}.`
    );
  }
  return items.length > 0 ? (
    <ul className="mb-0 pl-3">
      {items.map((text, i) => (
        <li key={i}>{text}</li>
      ))}
    </ul>
  ) : (
    <span>Subtotal + Tax equals Total, and line items sum to Subtotal.</span>
  );
}

// Short inline summary ("Subtotal $X + Tax $Y ≠ Total $Z") shown next to the
// warning badge so users see the math at a glance without hovering. The full
// breakdown (including line-item sums) stays in the popover.
function buildInlineSummary(c: InvoiceConsistency): string | null {
  if (c.isConsistent) return null;
  if (c.headerMismatch) {
    return `Subtotal $${c.subTotal.toFixed(2)} + Tax $${c.tax.toFixed(
      2,
    )} ≠ Total $${c.total.toFixed(2)}`;
  }
  if (c.linesMismatch) {
    return `Line items $${c.linesSum.toFixed(
      2,
    )} ≠ Subtotal $${c.subTotal.toFixed(2)}`;
  }
  return null;
}

export const InvoiceConsistencyBadge: React.FC<Props> = ({ invoice, publishLive = false }) => {
  const c = checkInvoiceConsistency(invoice);
  const dispatch = useDispatch();
  const isApplicable = c.isApplicable;
  const isConsistent = c.isConsistent;

  // Publish live reconciliation to the store (only from the designated live
  // badge). Runs only when the applicable/consistent booleans flip, so it does
  // not churn on every keystroke. Cleared on unmount so a stale value can't
  // outlive the editor. Hooks stay above the early return to satisfy the rules.
  React.useEffect(() => {
    if (!publishLive) return;
    dispatch(setLiveConsistency({ isApplicable, isConsistent }));
  }, [publishLive, isApplicable, isConsistent, dispatch]);
  React.useEffect(() => {
    if (!publishLive) return undefined;
    return () => {
      dispatch(setLiveConsistency(null));
    };
  }, [publishLive, dispatch]);

  if (!c.isApplicable) return null;

  const consistent = c.isConsistent;
  const popover = (
    <Popover id="invoice-consistency-popover">
      <Popover.Title>
        {consistent ? "Totals match" : "Numbers don't add up"}
      </Popover.Title>
      <Popover.Content>{buildDetails(c)}</Popover.Content>
    </Popover>
  );

  const inlineSummary = buildInlineSummary(c);

  return (
    <span className="d-inline-flex align-items-center flex-wrap">
      <OverlayTrigger placement="bottom" overlay={popover}>
        <Badge
          variant={consistent ? "success" : "warning"}
          className="p-1 pr-2"
          data-cy={
            consistent
              ? "invoice-consistency-ok"
              : "invoice-consistency-warning"
          }
          style={{ cursor: "help" }}
        >
          <FontAwesomeIcon
            color="white"
            icon={consistent ? faCheck : faExclamationTriangle}
          />
          <span className="ml-2 text-white">
            {consistent
              ? "Totals match"
              : "Numbers don't add up — review"}
          </span>
        </Badge>
      </OverlayTrigger>
      {inlineSummary && (
        <span
          className="ml-2 small text-muted"
          style={{ fontStyle: "italic" }}
        >
          {inlineSummary}
        </span>
      )}
    </span>
  );
};

export default InvoiceConsistencyBadge;
